import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { ThemeProvider } from './context/ThemeContext';
import ScrollToTop from './components/utils/ScrollToTop';
import LoadingBar from './components/common/LoadingBar';
import Home from './pages/Home';
import ChatListPage from './pages/ChatListPage';
import ChatRoomPage from './pages/ChatRoomPage';

import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import UploadProfileImage from './pages/UploadProfileImage';
import Teahers from './pages/Teachers'; // Typo: Should be Teachers
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Contact from './pages/Contact';
import About from './pages/About';
import Faq from './pages/Faq';
import Help from './pages/Help';
import { pageview } from './utils/gtagPageView';
import ProtectedRoute from './utils/ProtectedRoute';
import { getUserRole } from './utils/auth';
import PaymentComplete from './pages/PaymentComplete';

// Lazy load pages for better performance
const Courses = lazy(() => import('./pages/Courses'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword')); // Import ResetPassword
const Profile = lazy(() => import('./pages/Profile'));
const CategoryListing = lazy(() => import('./pages/CategoryListing'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const NotificationList = lazy(() => import('./pages/NotificationList')); // Import Notification page

// Admin Pages
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTeachers = lazy(() => import('./pages/admin/AdminTeachers'));
const AdminAnnouncements = lazy(() => import('./pages/admin/AdminAnnouncements'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminRequests = lazy(() => import('./pages/admin/AdminRequests'));
const AdminMail = lazy(() => import('./pages/admin/AdminMail'));

// Loading component
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-12 h-12 border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
  </div>
);

function AppContent() {
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ScrollToTop />
      <LoadingBar isLoading={loading} />
      <div className="flex flex-col min-h-screen transition-colors duration-200 dark:bg-gray-900">
        <Navbar />
        <main className="flex-grow">
          {loading ? (
            <PageLoading />
          ) : (
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:id" element={<CourseDetail />} />
                <Route path="/category/:category" element={<CategoryListing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} /> {/* Add route for ResetPassword */}
                {/* Updated profile routes */}
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/:identifier" element={<Profile />} />
                <Route path="/profile/id/:userId" element={<Profile />} />
                <Route path="/profile/username/:username" element={<Profile />} />
                <Route path="/chat" element={<ChatListPage />} />
                {/* <Route path="/dashboard" element={<TeacherDashboard />} />*/}

                <Route
                  path="/teacher-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ROLE_TEACHER']} userRole={getUserRole()}>
                      <TeacherDashboard />
                    </ProtectedRoute>
                  }
                />   <Route
                  path="/student-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ROLE_STUDENT']} userRole={getUserRole()}>
                      <StudentDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute allowedRoles={['ROLE_ADMIN']} userRole={getUserRole()}>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="teachers" element={<AdminTeachers />} />
                  <Route path="announcements" element={<AdminAnnouncements />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="requests" element={<AdminRequests />} />
                  <Route path="mail" element={<AdminMail />} />
                </Route>

                <Route
                  path="/student-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ROLE_STUDENT']} userRole={getUserRole()}>
                      <StudentDashboard />
                    </ProtectedRoute>
                  }
                />


                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/upload-profile-image" element={<UploadProfileImage />} />
                <Route path="/teachers" element={<Teahers />} /> {/* Consider fixing typo Teahers -> Teachers */}
                <Route path="/settings" element={<Settings />} />
                <Route path="/notifications" element={<NotificationList />} /> {/* Add route for Notification page */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/help" element={<Help />} />
              </Routes>
            </Suspense>
          )}
        </main>
        <Footer />
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;